import axios from 'axios';
import { toaster } from './components/common/Toaster';
import { PortalApiToken } from './services/api/api-token';
import { checkIfServerError } from './actions/AuthActionsAPI';
import { logoutUser } from './actions/LogoutAction';
import { LocalStorage } from './store/local-storage';
import i18n from './i18n';
import { generateKey } from './components/common/helper';

export const axiosInterceptorInstance = axios.create();

/**
 * Refresh the token and resend the previous request given in paramter
 * @param {object} previousRequestConfig
 * @returns a promise of the response of axios resent request
 */

export const retryRequest = (error, retries = 3) => {
    if (retries > 0) {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(axios(error.config));
            }, 100);
        }).catch((err) => retryRequest(err, retries - 1));
    }
    throw error;
};


export const logoutUserFromApp = () => {
    logoutUser();
    PortalApiToken.resetTokenData();
    LocalStorage.clearAuthCredentials();
    window?.location?.reload(false);
    toaster(i18n.t('translation:pages.toaster_messages.session_has_expired'));
    window.location.href = '/login';
};

axiosInterceptorInstance.interceptors.request.use(
    (request) => {
        request.headers['Authorization'] = 'Bearer ' + PortalApiToken.getApiToken();
        request.headers['Content-Type'] = 'application/json;charset=UTF-8';
        return request;
    },
    (error) => {
        return Promise.reject(error);
    }
);
export const handleResponseInterceptor = (response) => {
    return response;
};

export const handleErrorInterceptor = (error) => {
    const isExpectedDurationExceeded = error?.config?.suppressDurationExceededToaster
        && error?.response?.data?.code === 'duration-exceeded';
    const showGenericToaster = error?.config?.showGenericToaster !== false && !isExpectedDurationExceeded;
    if (!error.response) {
        if (showGenericToaster) {
            toaster(i18n.t('translation:pages.toaster_messages.something_went_wrong'));
        }
        throw error;
    }

    let hasTranslatedError = false;
    const responseMessage = error?.response?.data?.message;
    if (error?.response?.status !== 401 && showGenericToaster && (checkIfServerError(error) || responseMessage)) {
        if (error?.response?.status !== 504 && responseMessage) {
            const toasterErrorsKey = generateKey(responseMessage);
            if (i18n.exists(toasterErrorsKey)) {
                toaster(i18n.t(toasterErrorsKey));
                hasTranslatedError = true;
            }
        }
    }

    switch (error.response.status) {
        case 500:
            if (showGenericToaster && !hasTranslatedError) {
                toaster(i18n.t('translation:pages.toaster_messages.internal_server_error'));
            }
            throw error;
        case 504:
            return retryRequest(error);
            
        case 401:
            setTimeout(() => {
                return logoutUserFromApp();
            }, 100);
            break;
        case 403:
        case 400:
        case 404:
            if (showGenericToaster && !hasTranslatedError) {
                toaster(i18n.t('translation:pages.toaster_messages.something_went_wrong'));
            }
            throw error;
        default:
            if (showGenericToaster && !hasTranslatedError) {
                toaster(i18n.t('translation:pages.toaster_messages.something_went_wrong'));
            }
            throw error;
    }
    
};

axiosInterceptorInstance.interceptors.response.use(handleResponseInterceptor, handleErrorInterceptor);

export default axiosInterceptorInstance;
